/**
 * Agent Rate Limiting Service
 * Extends RateLimitService to provide AI agent-specific rate limiting and quota management
 */

import { RateLimitService, RateLimitConfig, RateLimitResult } from './RateLimitService';

export interface AgentRateLimitConfig extends RateLimitConfig {
  agentId: string;
  perMinuteLimit: number;
  perHourLimit: number;
  perDayLimit: number;
  monthlyQuotas: {
    requests: number;
    campaigns: number;
    leads: number;
    emails: number;
  };
  burstAllowance?: number;
  backoffPolicy?: {
    enabled: boolean;
    multiplier: number;
    maxDelay: number;
  };
}

export interface AgentQuotaUsage {
  requests: number;
  campaigns: number;
  leads: number;
  emails: number;
  resetDate: Date;
}

export interface AgentRateLimitStatus {
  agentId: string;
  perMinute: RateLimitResult;
  perHour: RateLimitResult;
  perDay: RateLimitResult;
  monthlyQuotas: {
    requests: { used: number; limit: number; percentage: number };
    campaigns: { used: number; limit: number; percentage: number };
    leads: { used: number; limit: number; percentage: number };
    emails: { used: number; limit: number; percentage: number };
  };
  status: 'active' | 'throttled' | 'quota_exceeded' | 'suspended';
  nextResetTime: Date;
}

export interface AgentUsageMetrics {
  agentId: string;
  timeframe: 'hour' | 'day' | 'week' | 'month';
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  rateLimitHits: number;
  quotaViolations: number;
  averageResponseTime: number;
  peakRequestsPerMinute: number;
  endpoints: {
    [endpoint: string]: {
      requests: number;
      avgResponseTime: number;
      errorRate: number;
    };
  };
}

export class AgentRateLimitService {
  private minuteService: RateLimitService;
  private hourService: RateLimitService;
  private dayService: RateLimitService;
  private quotaUsage: Map<string, AgentQuotaUsage> = new Map();
  private agentConfigs: Map<string, AgentRateLimitConfig> = new Map();
  private usageMetrics: Map<string, AgentUsageMetrics> = new Map();

  constructor() {
    // Initialize rate limit services for different time windows
    this.minuteService = new RateLimitService({
      windowMs: 60 * 1000, // 1 minute
      maxRequests: 100, // Default, will be overridden per agent
      keyGenerator: (agentId: string) => `minute:${agentId}`
    });

    this.hourService = new RateLimitService({
      windowMs: 60 * 60 * 1000, // 1 hour
      maxRequests: 1000, // Default, will be overridden per agent
      keyGenerator: (agentId: string) => `hour:${agentId}`
    });

    this.dayService = new RateLimitService({
      windowMs: 24 * 60 * 60 * 1000, // 1 day
      maxRequests: 10000, // Default, will be overridden per agent
      keyGenerator: (agentId: string) => `day:${agentId}`
    });

    // Start cleanup interval for quota usage
    setInterval(() => {
      this.cleanupExpiredQuotas();
    }, 60 * 60 * 1000); // Every hour
  }

  /**
   * Register an agent with specific rate limit configuration
   */
  async registerAgent(config: AgentRateLimitConfig): Promise<void> {
    this.agentConfigs.set(config.agentId, config);
    
    // Initialize quota usage for the agent
    const now = new Date();
    const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1); // First day of next month
    
    this.quotaUsage.set(config.agentId, {
      requests: 0,
      campaigns: 0,
      leads: 0,
      emails: 0,
      resetDate
    });

    // Initialize usage metrics
    this.usageMetrics.set(config.agentId, {
      agentId: config.agentId,
      timeframe: 'hour',
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      rateLimitHits: 0,
      quotaViolations: 0,
      averageResponseTime: 0,
      peakRequestsPerMinute: 0,
      endpoints: {}
    });
  }

  /**
   * Simple rate limit check for agent API gateway
   */
  async checkAgentRateLimit(agentId: string): Promise<{ allowed: boolean; remaining: number; resetTime: number }> {
    const config = this.agentConfigs.get(agentId);
    if (!config) {
      // If agent not registered, allow with default limits
      return {
        allowed: true,
        remaining: 1000,
        resetTime: Math.floor((Date.now() + 60000) / 1000) // 1 minute from now
      };
    }

    // Check per-minute rate limit
    const perMinute = await this.minuteService.checkRateLimit(agentId);
    
    return {
      allowed: perMinute.allowed,
      remaining: perMinute.remaining,
      resetTime: Math.floor(perMinute.resetTime / 1000)
    };
  }

  /**
   * Check comprehensive rate limits for an agent request
   */
  async checkComprehensiveAgentRateLimit(
    agentId: string, 
    endpoint?: string, 
    resourceType?: 'requests' | 'campaigns' | 'leads' | 'emails'
  ): Promise<AgentRateLimitStatus> {
    const config = this.agentConfigs.get(agentId);
    if (!config) {
      throw new Error(`Agent ${agentId} not registered`);
    }

    // Update service configurations for this agent
    this.minuteService['config'].maxRequests = config.perMinuteLimit;
    this.hourService['config'].maxRequests = config.perHourLimit;
    this.dayService['config'].maxRequests = config.perDayLimit;

    // Check all time-based rate limits
    const [perMinute, perHour, perDay] = await Promise.all([
      this.minuteService.checkRateLimit(agentId, endpoint),
      this.hourService.checkRateLimit(agentId, endpoint),
      this.dayService.checkRateLimit(agentId, endpoint)
    ]);

    // Check monthly quotas
    const quotaUsage = this.quotaUsage.get(agentId)!;
    const monthlyQuotas = {
      requests: {
        used: quotaUsage.requests,
        limit: config.monthlyQuotas.requests,
        percentage: Math.round((quotaUsage.requests / config.monthlyQuotas.requests) * 100)
      },
      campaigns: {
        used: quotaUsage.campaigns,
        limit: config.monthlyQuotas.campaigns,
        percentage: Math.round((quotaUsage.campaigns / config.monthlyQuotas.campaigns) * 100)
      },
      leads: {
        used: quotaUsage.leads,
        limit: config.monthlyQuotas.leads,
        percentage: Math.round((quotaUsage.leads / config.monthlyQuotas.leads) * 100)
      },
      emails: {
        used: quotaUsage.emails,
        limit: config.monthlyQuotas.emails,
        percentage: Math.round((quotaUsage.emails / config.monthlyQuotas.emails) * 100)
      }
    };

    // Determine overall status
    let status: 'active' | 'throttled' | 'quota_exceeded' | 'suspended' = 'active';
    
    // Check if any quota is exceeded
    if (resourceType && monthlyQuotas[resourceType].percentage >= 100) {
      status = 'quota_exceeded';
    } else if (Object.values(monthlyQuotas).some(quota => quota.percentage >= 100)) {
      status = 'quota_exceeded';
    } else if (!perMinute.allowed || !perHour.allowed || !perDay.allowed) {
      status = 'throttled';
    }

    // Calculate next reset time
    const nextResetTime = new Date(Math.min(
      perMinute.windowStart.getTime() + 60 * 1000,
      perHour.windowStart.getTime() + 60 * 60 * 1000,
      perDay.windowStart.getTime() + 24 * 60 * 60 * 1000,
      quotaUsage.resetDate.getTime()
    ));

    return {
      agentId,
      perMinute,
      perHour,
      perDay,
      monthlyQuotas,
      status,
      nextResetTime
    };
  }

  /**
   * Record a successful request and update quotas
   */
  async recordAgentRequest(
    agentId: string,
    endpoint: string,
    resourceType: 'requests' | 'campaigns' | 'leads' | 'emails' = 'requests',
    responseTime?: number,
    success: boolean = true
  ): Promise<void> {
    // Update quota usage
    const quotaUsage = this.quotaUsage.get(agentId);
    if (quotaUsage) {
      quotaUsage[resourceType]++;
      
      // Check if quota reset is needed
      const now = new Date();
      if (now >= quotaUsage.resetDate) {
        this.resetMonthlyQuotas(agentId);
      }
    }

    // Update usage metrics
    const metrics = this.usageMetrics.get(agentId);
    if (metrics) {
      metrics.totalRequests++;
      if (success) {
        metrics.successfulRequests++;
      } else {
        metrics.failedRequests++;
      }

      // Update endpoint-specific metrics
      if (!metrics.endpoints[endpoint]) {
        metrics.endpoints[endpoint] = {
          requests: 0,
          avgResponseTime: 0,
          errorRate: 0
        };
      }
      
      const endpointMetrics = metrics.endpoints[endpoint];
      endpointMetrics.requests++;
      
      if (responseTime) {
        endpointMetrics.avgResponseTime = 
          (endpointMetrics.avgResponseTime * (endpointMetrics.requests - 1) + responseTime) / 
          endpointMetrics.requests;
      }
      
      endpointMetrics.errorRate = 
        (endpointMetrics.requests - (success ? 1 : 0)) / endpointMetrics.requests;
    }

    // Record in underlying rate limit services
    await Promise.all([
      this.minuteService.recordRequestResult(agentId, success, endpoint),
      this.hourService.recordRequestResult(agentId, success, endpoint),
      this.dayService.recordRequestResult(agentId, success, endpoint)
    ]);
  }

  /**
   * Get comprehensive usage statistics for an agent
   */
  async getAgentUsageStats(agentId: string): Promise<AgentUsageMetrics | null> {
    return this.usageMetrics.get(agentId) || null;
  }

  /**
   * Get all agents' rate limit status
   */
  async getAllAgentsStatus(): Promise<AgentRateLimitStatus[]> {
    const statuses: AgentRateLimitStatus[] = [];
    
    for (const agentId of Array.from(this.agentConfigs.keys())) {
      try {
        const status = await this.checkComprehensiveAgentRateLimit(agentId);
        statuses.push(status);
      } catch (error) {
        console.error(`Failed to get status for agent ${agentId}:`, error);
      }
    }
    
    return statuses;
  }

  /**
   * Update agent rate limit configuration
   */
  async updateAgentConfig(agentId: string, config: Partial<AgentRateLimitConfig>): Promise<void> {
    const existingConfig = this.agentConfigs.get(agentId);
    if (!existingConfig) {
      throw new Error(`Agent ${agentId} not found`);
    }

    const updatedConfig = { ...existingConfig, ...config };
    this.agentConfigs.set(agentId, updatedConfig);
  }

  /**
   * Reset monthly quotas for an agent
   */
  async resetMonthlyQuotas(agentId: string): Promise<void> {
    const quotaUsage = this.quotaUsage.get(agentId);
    if (quotaUsage) {
      const now = new Date();
      quotaUsage.requests = 0;
      quotaUsage.campaigns = 0;
      quotaUsage.leads = 0;
      quotaUsage.emails = 0;
      quotaUsage.resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }
  }

  /**
   * Get quota usage for an agent
   */
  async getAgentQuotaUsage(agentId: string): Promise<AgentQuotaUsage | null> {
    return this.quotaUsage.get(agentId) || null;
  }

  /**
   * Suspend an agent (block all requests)
   */
  async suspendAgent(agentId: string): Promise<void> {
    const config = this.agentConfigs.get(agentId);
    if (config) {
      config.perMinuteLimit = 0;
      config.perHourLimit = 0;
      config.perDayLimit = 0;
    }
  }

  /**
   * Reactivate a suspended agent
   */
  async reactivateAgent(agentId: string, originalConfig: AgentRateLimitConfig): Promise<void> {
    this.agentConfigs.set(agentId, originalConfig);
  }

  /**
   * Get rate limit middleware for Express.js
   */
  getRateLimitMiddleware() {
    return async (req: any, res: any, next: any) => {
      try {
        const agentId = req.headers['x-agent-id'] || req.query.agentId;
        if (!agentId) {
          return res.status(401).json({
            error: {
              code: 'MISSING_AGENT_ID',
              message: 'Agent ID is required'
            }
          });
        }

        const status = await this.checkComprehensiveAgentRateLimit(agentId, req.path);
        
        // Add rate limit headers
        res.set({
          'X-RateLimit-Limit-Minute': status.perMinute.totalRequests.toString(),
          'X-RateLimit-Remaining-Minute': status.perMinute.remaining.toString(),
          'X-RateLimit-Reset-Minute': status.perMinute.resetTime.toString(),
          'X-RateLimit-Limit-Hour': status.perHour.totalRequests.toString(),
          'X-RateLimit-Remaining-Hour': status.perHour.remaining.toString(),
          'X-RateLimit-Reset-Hour': status.perHour.resetTime.toString(),
          'X-RateLimit-Limit-Day': status.perDay.totalRequests.toString(),
          'X-RateLimit-Remaining-Day': status.perDay.remaining.toString(),
          'X-RateLimit-Reset-Day': status.perDay.resetTime.toString()
        });

        if (status.status === 'quota_exceeded') {
          return res.status(429).json({
            error: {
              code: 'QUOTA_EXCEEDED',
              message: 'Monthly quota exceeded',
              details: status.monthlyQuotas,
              retryAfter: Math.ceil((status.nextResetTime.getTime() - Date.now()) / 1000)
            }
          });
        }

        if (status.status === 'throttled') {
          return res.status(429).json({
            error: {
              code: 'RATE_LIMIT_EXCEEDED',
              message: 'Rate limit exceeded',
              retryAfter: Math.min(
                status.perMinute.resetTime,
                status.perHour.resetTime,
                status.perDay.resetTime
              )
            }
          });
        }

        if (status.status === 'suspended') {
          return res.status(403).json({
            error: {
              code: 'AGENT_SUSPENDED',
              message: 'Agent access has been suspended'
            }
          });
        }

        // Store agent ID and status for later use
        req.agentId = agentId;
        req.rateLimitStatus = status;
        
        next();
      } catch (error) {
        console.error('Rate limit middleware error:', error);
        res.status(500).json({
          error: {
            code: 'RATE_LIMIT_ERROR',
            message: 'Internal rate limiting error'
          }
        });
      }
    };
  }

  /**
   * Clean up expired quota entries
   */
  private cleanupExpiredQuotas(): void {
    const now = new Date();
    
    for (const [agentId, quotaUsage] of Array.from(this.quotaUsage.entries())) {
      if (now >= quotaUsage.resetDate) {
        this.resetMonthlyQuotas(agentId);
      }
    }
  }

  /**
   * Destroy the service and cleanup resources
   */
  destroy(): void {
    this.minuteService.destroy();
    this.hourService.destroy();
    this.dayService.destroy();
    this.quotaUsage.clear();
    this.agentConfigs.clear();
    this.usageMetrics.clear();
  }
}